import { CartRuleEngine } from 'cart-rule-engine';
import { fetchCart } from 'theme';
class CartThemeConnector {
	constructor() {
		this.buildEngine();
		document.addEventListener('cart:change', this.onCartChange.bind(this));
		document.addEventListener('ablyft:variant-ready', this.onVariantReady.bind(this));
		this.cartDrawer = document.querySelector('cart-drawer');
		this.initCartDrawerEvents();
	}
	buildEngine() {
		const cartSettings = window.cartSettings || {};
		const abTestingVariant = window.activeAbTestingIdentifier || null;
		this.builtWithVariant = abTestingVariant;

		const filteredRules = (cartSettings.rules || []).filter((rule) => {
			if (!rule.abTestVariant) return true;
			return rule.abTestVariant === abTestingVariant;
		});

		this.cartRuleEngine = new CartRuleEngine({
			rules: filteredRules || [],
			successMessage: cartSettings.successMessage || '',
			displayMode: cartSettings.displayMode || 'progress_bar',
			textJoiner: cartSettings.textJoiner || ' und ',
			showAllTexts: cartSettings.showAllTexts || false,
		});
	}
	async onVariantReady() {
		const activeVariant = window.activeAbTestingIdentifier || null;
		if (activeVariant === this.builtWithVariant) return;
		this.buildEngine();
		const engine = this.cartRuleEngine;
		try {
			await this.refreshBarState();
			if (engine !== this.cartRuleEngine) return;
			if (this.payloads.length > 0) {
				this.updateItemsInCart({ updatePayload: this.transformPayloads(this.payloads) });
			} else {
				document.dispatchEvent(new CustomEvent('cart:refresh'));
			}
		} catch (error) {
			console.error('Unable to refresh cart rules for ABLyft variant:', error);
		}
	}
	initCartDrawerEvents() {
		if (!this.cartDrawer) return;
		this.cartDrawer.addEventListener('dialog:before-show', this.setupProgressBarAnimation.bind(this));
		this.cartDrawer.addEventListener('dialog:after-show', this.animateProgressBar.bind(this));
		this.cartDrawer.addEventListener('dialog:before-show', this.syncActiveRulesFromTime.bind(this));
		this.cartDrawer.addEventListener('cart-drawer:refreshed', this.syncActiveRulesFromTime.bind(this));
		this.cartDrawer.addEventListener('cart-drawer:refreshed', this.refreshBarState.bind(this));
	}
	async refreshBarState() {
		await this.updateValues();
		this.bar = this.cartDrawer?.querySelector('cart-bar');
		this.bar?.initBarStepPositions();
		await this.bar?.renderText();
		await this.bar?.renderActive();
		if (this.cartRuleEngine.displayMode !== 'milestones') this.handleProgressBarRefresh();
	}
	// updates the bar and cart when a rule becomes active or inactive
	async syncActiveRulesFromTime() {
		const timeBoundRules = this.cartRuleEngine.rules.filter((rule) => rule.startTime || rule.endTime);
		if (timeBoundRules.length === 0) {
			return;
		}

		const changes = timeBoundRules.map((rule) => {
			const wasActive = rule.isActive;
			rule.isActive = rule.computeIsActive();
			return wasActive !== rule.isActive;
		});
		if (!changes.includes(true)) {
			return;
		}

		this.cartRuleEngine.initRulePositions();
		await this.updateValues();

		if (this.payloads && this.payloads.length > 0) {
			// sends a real cart update, which also refreshes the section HTML on its own
			const updatePayload = this.transformPayloads(this.payloads);
			this.updateItemsInCart({ updatePayload });
			console.log('cart updated with payloads', updatePayload);
			return;
		}

		// if no payloads, just refresh the cart to update the bar and text
		document.dispatchEvent(new CustomEvent('cart:refresh'));
		console.log('cart refreshed because a rule changed activity');
	}
	async setupProgressBarAnimation() {
		if (this.cartRuleEngine.displayMode === 'milestones') return;
		await this.updateCartBarProgress();
		this.bar = this.cartDrawer.querySelector('cart-bar');
		this.bar?.setProgressWidth(0);
	}
	animateProgressBar() {
		if (this.cartRuleEngine.displayMode === 'milestones') return;
		this.bar = this.cartDrawer.querySelector('cart-bar');
		this.bar?.animateProgressBar(0, this.cartBarProgress);
		this.oldCartBarProgress = this.cartBarProgress;
	}
	handleProgressBarRefresh() {
		if (this.cartRuleEngine.displayMode === 'milestones') return;
		this.bar = this.cartDrawer?.querySelector('cart-bar');
		if (this.oldCartBarProgress === this.cartBarProgress) {
			this.bar?.setProgressWidth(this.oldCartBarProgress);
			return;
		}
		this.bar?.animateProgressBar(this.oldCartBarProgress || 0, this.cartBarProgress);
		this.oldCartBarProgress = this.cartBarProgress;
	}
	transformPayloads(payload) {
		const updates = {};
		payload.forEach((item) => {
			updates[item.key || item.id] = item.quantity;
		});
		return updates;
	}
	updateItemsInCart({ updatePayload }) {
		const sectionsToBundle = new Set();
		document.documentElement.dispatchEvent(
			new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }),
		);
		const data = {
			updates: updatePayload,
			sections: [...sectionsToBundle].join(','),
		};
		fetch(`${Shopify.routes.root}cart/update.js`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		})
			.then(async (response) => {
				if (response.ok) {
					const cartContent = await response.json();
					// document.dispatchEvent(new CustomEvent('cart:refresh'));
					document.documentElement.dispatchEvent(
						new CustomEvent('cart:change', {
							bubbles: true,
							detail: {
								baseEvent: 'line-item:change',
								cart: cartContent,
							},
						}),
					);
				}
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	}
	getRulePositions() {
		return this.cartRuleEngine.getRulePositions();
	}
	async updateValues(cart) {
		const currentCart = cart ? cart : await fetchCart;
		const { qualifiedRules, payloads, formattedText, shippingPrice, cartBarProgress } =
			this.cartRuleEngine.processCartRules(currentCart);
		//this.payloads = payloads;
		this.payloads = [...payloads];
		this.qualifiedRules = qualifiedRules;
		this.formattedText = formattedText;
		this.shippingPrice = shippingPrice;
		this.cartBarProgress = cartBarProgress;
	}
	async getFormattedText() {
		if (this.formattedText) {
			return this.formattedText;
		}
		await this.updateValues();
		return this.formattedText;
	}
	async getQualifiedRules() {
		if (this.qualifiedRules) {
			return this.qualifiedRules;
		}
		await this.updateValues();
		return this.qualifiedRules;
	}
	async updateCartBarProgress() {
		if (this.cartBarProgress) {
			await this.updateValues();
		}
	}

	async onCartChange(event) {
		const cart = event.detail.cart;
		this.updateValues(cart);
		if (!this.payloads || this.payloads.length === 0) {
			return;
		}
		const updatePayload = this.transformPayloads(this.payloads);
		this.updateItemsInCart({
			updatePayload,
		});
	}
}
window.themeConnector = new CartThemeConnector();
export { CartThemeConnector };
